import { useState, useEffect, useCallback } from 'react';
import { MessageCircleQuestion, X } from 'lucide-react';
import { useAuth } from '../../App';
import {
  getStationFeedbacks,
  getMyFeedbacks,
  type Feedback,
} from '../../api/client';
import TicketList from './TicketList';
import ChatThread from './ChatThread';
import NewTicketForm from './NewTicketForm';

type DrawerView = 'list' | 'chat' | 'new';

export default function FeedbackDrawer() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<DrawerView>('list');
  const [activeTicketId, setActiveTicketId] = useState<string | null>(null);
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(false);

  const isStation = !!user?.stationId;

  const ruckfrageCount = feedbacks.filter((f) => f.status === 'RUCKFRAGE').length;

  const loadFeedbacks = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      let data: Feedback[] = [];
      if (isStation && user.stationId) {
        data = await getStationFeedbacks(user.stationId);
      } else if (user.userId) {
        data = await getMyFeedbacks();
      }

      // Sortierung: letzte Aktivitaet zuerst
      data.sort((a, b) => {
        const lastA = a.comments.length > 0
          ? new Date(a.comments[a.comments.length - 1].createdAt).getTime()
          : new Date(a.updatedAt).getTime();
        const lastB = b.comments.length > 0
          ? new Date(b.comments[b.comments.length - 1].createdAt).getTime()
          : new Date(b.updatedAt).getTime();
        return lastB - lastA;
      });
      setFeedbacks(data);
    } catch (e) {
      console.error('Failed to load feedbacks:', e);
    } finally {
      setLoading(false);
    }
  }, [user, isStation]);

  // Initial laden fuer Badge-Count (auch ohne Drawer offen)
  useEffect(() => {
    loadFeedbacks();
  }, [loadFeedbacks]);

  // Beim Oeffnen des Drawers neu laden
  useEffect(() => {
    if (isOpen) {
      loadFeedbacks();
    }
  }, [isOpen, loadFeedbacks]);

  const handleOpenTicket = (ticketId: string) => {
    setActiveTicketId(ticketId);
    setView('chat');
  };

  const handleBack = () => {
    setView('list');
    setActiveTicketId(null);
  };

  const handleNewTicket = () => {
    setView('new');
  };

  const handleTicketCreated = () => {
    loadFeedbacks();
    setView('list');
  };

  const handleCommentAdded = () => {
    loadFeedbacks();
  };

  const activeTicket = feedbacks.find((f) => f.id === activeTicketId) || null;

  if (!user) return null;

  return (
    <>
      {/* FAB - nur sichtbar wenn Drawer geschlossen */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          // Sitzt über der Fußzeile (AppFooter), damit die Version rechts
          // unten frei bleibt - mobil zusätzlich über der Navigationsleiste.
          className={`fixed z-50 rounded-full px-4 py-2.5 shadow-lg transition-all duration-200 hover:scale-105 flex items-center gap-2 bottom-32 right-4 lg:bottom-16 lg:right-6 animate-[bounce_1s_ease-in-out_3] ${
            ruckfrageCount > 0
              ? 'bg-amber-500 text-white'
              : 'bg-slate-700 text-white'
          }`}
          aria-label="Hilfe und Meldung"
        >
          <MessageCircleQuestion className="w-5 h-5" />
          <span className="text-sm font-medium">Hilfe & Meldung</span>
          {ruckfrageCount > 0 && (
            <span className="bg-white text-amber-600 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {ruckfrageCount}
            </span>
          )}
        </button>
      )}

      {/* Drawer */}
      <div
        data-feedback-drawer
        className={`fixed top-0 right-0 h-full w-full sm:w-[450px] bg-white shadow-2xl z-40 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {view === 'list' && (
            <TicketList
              feedbacks={feedbacks}
              loading={loading}
              onOpenTicket={handleOpenTicket}
              onNewTicket={handleNewTicket}
              onClose={() => setIsOpen(false)}
              onRefresh={loadFeedbacks}
            />
          )}
          {view === 'chat' && activeTicket && (
            <ChatThread
              feedback={activeTicket}
              onBack={handleBack}
              onCommentAdded={handleCommentAdded}
            />
          )}
          {view === 'new' && (
            <NewTicketForm
              onBack={handleBack}
              onCreated={handleTicketCreated}
            />
          )}
        </div>
      </div>

      {/* Backdrop (mobile) */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/20 sm:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
}
